/**
 * Cost Breakdown Component
 * 
 * Displays cost breakdown by model type using pie/doughnut charts
 * with Plotly.js for visualization.
 * 
 * Implements Requirement 5.4: Show estimated costs for current and historical usage
 */

import React, { useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist-min';
import { ChartData } from '../../services/QuotaAnalyticsService';
import quotaAnalyticsService from '../../services/QuotaAnalyticsService';
import styles from './CostBreakdown.module.css';

interface CostBreakdownProps {
  chartData: ChartData;
}

export const CostBreakdown: React.FC<CostBreakdownProps> = ({ chartData }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current || !chartData.labels.length) return;

    const dataset = chartData.datasets[0];
    const values = dataset.data;
    const labels = chartData.labels;
    const colors = Array.isArray(dataset.backgroundColor) 
      ? dataset.backgroundColor 
      : generateColors(labels.length);

    const trace: Plotly.Data = {
      type: 'pie',
      values: values,
      labels: labels,
      hole: 0.5, // Doughnut chart
      marker: {
        colors: colors,
        line: {
          color: 'var(--bg-primary)',
          width: 2
        }
      },
      textinfo: 'percent',
      textposition: 'outside',
      textfont: {
        size: 11,
        color: 'var(--text-secondary)'
      },
      hovertemplate: '<b>%{label}</b><br>Cost: $%{value:.4f}<br>%{percent}<extra></extra>',
      pull: 0.02
    };

    const layout: Partial<Plotly.Layout> = {
      autosize: true,
      height: 280,
      margin: { l: 20, r: 20, t: 20, b: 20 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      font: {
        family: 'Inter, system-ui, sans-serif',
        color: 'var(--text-secondary)'
      },
      showlegend: true,
      legend: {
        orientation: 'v',
        x: 1,
        y: 0.5,
        xanchor: 'left',
        font: { size: 11 }
      },
      annotations: [
        {
          text: `$${values.reduce((a, b) => a + b, 0).toFixed(2)}`,
          x: 0.5,
          y: 0.5,
          font: {
            size: 16,
            color: 'var(--text-primary)',
            family: 'Inter, system-ui, sans-serif'
          },
          showarrow: false
        }
      ]
    };

    const config: Partial<Plotly.Config> = {
      responsive: true,
      displayModeBar: false,
      staticPlot: false
    };

    Plotly.newPlot(chartRef.current, [trace], layout, config);

    // Cleanup - copy ref to local variable to avoid stale closure
    const currentChartRef = chartRef.current;
    return () => {
      if (currentChartRef) {
        Plotly.purge(currentChartRef);
      }
    };
  }, [chartData]);

  if (!chartData.labels.length || !chartData.datasets[0]?.data.length) {
    return (
      <div className={styles.noData}>
        <p>No cost data available</p>
      </div>
    );
  }

  const dataset = chartData.datasets[0];
  const totalCost = dataset.data.reduce((a, b) => a + b, 0);

  return (
    <div className={styles.container}>
      <div ref={chartRef} className={styles.chart} />
      
      {/* Cost Legend */}
      <div className={styles.legend}>
        {chartData.labels.map((label, index) => {
          const cost = dataset.data[index];
          const percentage = totalCost > 0 ? (cost / totalCost) * 100 : 0;
          const colors = Array.isArray(dataset.backgroundColor) 
            ? dataset.backgroundColor 
            : generateColors(chartData.labels.length);
          
          return (
            <div key={label} className={styles.legendItem}>
              <div 
                className={styles.legendColor}
                style={{ backgroundColor: colors[index] }}
              />
              <span className={styles.legendLabel}>{label}</span>
              <span className={styles.legendValue}>
                {quotaAnalyticsService.formatCost(cost)}
              </span>
              <span className={styles.legendPercentage}>
                ({quotaAnalyticsService.formatPercentage(percentage)})
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Generate colors for the chart
function generateColors(count: number): string[] {
  const baseColors = [
    '#ffffff',
    '#d4d4d4',
    '#a3a3a3',
    '#737373',
    '#525252',
    '#404040',
    '#262626',
    '#e5e5e5',
    '#b8b8b8',
    '#888888',
  ];

  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(baseColors[i % baseColors.length]);
  }
  return colors;
}

export default CostBreakdown;
